// src/pages/ResetPasswordPage.js
import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./LoginPage.css";

const LOGO_ICON_SRC = "/logo-icon.png";
const LOGO_TEXT_SRC = "/logo-text-black.png";

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

export default function ResetPasswordPage() {
  const nav = useNavigate();
  const auth = useAuth();
  const [sp] = useSearchParams();

  const tokenFromUrl = safeString(sp.get("token")).trim();
  const emailFromUrl = safeString(sp.get("email")).trim();

  const [token] = useState(tokenFromUrl);
  const [email] = useState(emailFromUrl);

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);

  const linkLooksValid = !!tokenFromUrl && !!emailFromUrl;

  const passwordsMatch = pw1 === pw2;
  const passwordLongEnough = pw1.length >= 8;

  const showLengthError = pw1.length > 0 && !passwordLongEnough;
  const showMatchError =
    pw1.length > 0 && pw2.length > 0 && !passwordsMatch;

  const canSubmit = useMemo(() => {
    if (!linkLooksValid) return false;
    if (!passwordLongEnough) return false;
    if (!passwordsMatch) return false;
    return !submitting;
  }, [linkLooksValid, passwordLongEnough, passwordsMatch, submitting]);

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) return;

    setError("");
    setOk(false);
    setSubmitting(true);

    try {
      await auth.apiPost("/api/auth/reset-password", {
        token,
        email,
        newPassword: pw1,
      });

      setOk(true);
      setTimeout(() => nav("/login", { replace: true }), 700);
    } catch (err) {
      setError(err?.message || "Could not reset password.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="ft-loginPage">
      <div className="ft-loginCard">
        <div className="ft-loginBrandWrap" aria-label="Flowtrak">
          <img
            src={LOGO_ICON_SRC}
            alt="Flowtrak icon"
            className="ft-loginLogoIcon"
            draggable={false}
          />
          <img
            src={LOGO_TEXT_SRC}
            alt="Flowtrak"
            className="ft-loginLogoText"
            draggable={false}
          />
        </div>

        <div style={{ textAlign: "center", marginBottom: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>Reset password</div>
          <div className="ft-subtle" style={{ marginTop: 4 }}>
            Enter a new password below.
          </div>
        </div>

        {!linkLooksValid && (
          <div className="ft-loginError" style={{ marginBottom: 10 }}>
            This reset link is invalid or expired. Please request a new reset email.
          </div>
        )}

        {error && (
          <div className="ft-loginError" style={{ marginBottom: 10 }}>
            {error}
          </div>
        )}

        {ok && (
          <div className="ft-modalSuccess" style={{ marginBottom: 10 }}>
            Password updated. Redirecting to sign in…
          </div>
        )}

        <form onSubmit={submit} className="ft-loginForm">
          {/* Hidden fields */}
          <input type="hidden" value={email} readOnly />
          <input type="hidden" value={token} readOnly />

          <div className="ft-loginField">
            <label className="ft-loginLabel">New password</label>
            <input
              className="ft-loginInput"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              type="password"
              autoComplete="new-password"
              placeholder="Minimum 8 characters"
              disabled={!linkLooksValid || submitting}
            />
            {showLengthError && (
              <div style={{ color: "#b91c1c", fontSize: 13, marginTop: 4 }}>
                Password must be at least 8 characters.
              </div>
            )}
          </div>

          <div className="ft-loginField">
            <label className="ft-loginLabel">Confirm password</label>
            <input
              className="ft-loginInput"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              type="password"
              autoComplete="new-password"
              placeholder="Type it again"
              disabled={!linkLooksValid || submitting}
            />
            {showMatchError && (
              <div style={{ color: "#b91c1c", fontSize: 13, marginTop: 4 }}>
                Passwords do not match.
              </div>
            )}
          </div>

          <button type="submit" className="ft-loginBtn" disabled={!canSubmit}>
            {submitting ? "Updating…" : "Update password"}
          </button>

          <div className="ft-loginFooter">
            <button
              type="button"
              className="ft-loginLinkBtn"
              onClick={() => nav("/login")}
              disabled={submitting}
            >
              Back to sign in
            </button>

            <div className="ft-loginCopyright">© 2026 Flowtrak</div>
          </div>
        </form>
      </div>
    </div>
  );
}